import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Instagram, Linkedin, Mail, Phone } from 'lucide-react';
import PublicPageShell from '../components/PublicPageShell';
import { useLanguage } from '../contexts/LanguageContext';
import { getLocalizedAboutPage } from '../data/contentLibrary';
import { CONTACT_CONFIG, getTelephoneLink } from '../config/contact';
import { FOUNDER_PROFILE } from '../config/founder';

const ListBlock = ({ title, items }) => (
  <section className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-6">
    <h2 className="text-2xl font-semibold text-white">{title}</h2>
    <ul className="mt-5 space-y-3 text-sm leading-6 text-white/62">
      {items.map((item) => (
        <li key={typeof item === 'string' ? item : item.label}>
          {typeof item === 'string' ? (
            item
          ) : (
            <Link to={item.path} className="hover:text-[#00FFD1]">
              {item.label}
            </Link>
          )}
        </li>
      ))}
    </ul>
  </section>
);

const AboutPage = () => {
  const { currentLang, t } = useLanguage();
  const about = getLocalizedAboutPage(currentLang);

  return (
    <PublicPageShell mainClassName="pt-20">
      <section className="relative overflow-hidden bg-black">
        <div className="hero-grid absolute inset-0 pointer-events-none" />
        <div className="relative mx-auto max-w-[1120px] px-5 py-16 md:px-[7.6923%] md:py-24">
          <nav className="mb-8 flex flex-wrap gap-2 text-sm text-white/44" aria-label={t('common.breadcrumb')}>
            <Link to="/" className="hover:text-[#00FFD1]">{t('nav.home')}</Link>
            <span>/</span>
            <span className="text-white/70">{t('nav.about')}</span>
          </nav>

          <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-16">
            <header className="max-w-4xl">
              <p className="mb-4 text-sm font-semibold uppercase text-[#00FFD1]">
                {t('common.entityEyebrow')}
              </p>
              <h1 className="text-4xl font-semibold leading-tight text-white md:text-6xl">
                {about.h1}
              </h1>
              <p className="mt-6 text-base leading-8 text-white/64 md:text-lg">
                {about.intro}
              </p>
            </header>

            <figure className="relative mx-auto w-full max-w-[430px] lg:mx-0">
              <div className="absolute -inset-3 rounded-[30px] border border-[#00FFD1]/15" />
              <div className="relative aspect-square overflow-hidden rounded-[26px] border border-white/[0.1] bg-white/[0.03] shadow-2xl shadow-black/50">
                <img
                  src={FOUNDER_PROFILE.image}
                  alt="Mustafa Can Arslan, founder and developer at Webouse"
                  width={FOUNDER_PROFILE.imageWidth}
                  height={FOUNDER_PROFILE.imageHeight}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover"
                />
                <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-5 pb-5 pt-16">
                  <p className="font-semibold text-white">{about.founder.name}</p>
                  <p className="mt-1 text-sm text-[#00FFD1]">{about.founder.role}</p>
                </div>
              </div>
            </figure>
          </div>

        <div className="mt-12 rounded-2xl border border-[#00FFD1]/20 bg-[#00FFD1]/[0.04] p-6 md:p-8">
          <p className="text-sm font-semibold uppercase tracking-[0.12em] text-[#00FFD1]">
            Founder-led from first call to launch
          </p>
          <h2 className="mt-3 text-2xl font-semibold text-white md:text-3xl">Independent by design.</h2>
          <p className="mt-5 max-w-3xl text-base leading-8 text-white/64">
            {about.founder.profile}
          </p>
          <p className="mt-4 max-w-3xl text-base leading-8 text-white/64">
            I also run{' '}
            <a
              href={FOUNDER_PROFILE.aeroCore}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[#00FFD1] hover:underline"
            >
              AeroCore Scripts
            </a>
            , where I build and sell aviation systems for FiveM servers.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href={FOUNDER_PROFILE.linkedIn}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-white/[0.1] bg-black/20 px-4 py-3 text-sm font-semibold text-white/72 hover:border-[#00FFD1]/35 hover:text-white"
            >
              <Linkedin className="h-4 w-4 text-[#00FFD1]" /> LinkedIn
              <ArrowUpRight className="h-4 w-4 text-white/35" />
            </a>
            <a
              href={FOUNDER_PROFILE.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-white/[0.1] bg-black/20 px-4 py-3 text-sm font-semibold text-white/72 hover:border-[#00FFD1]/35 hover:text-white"
            >
              <Instagram className="h-4 w-4 text-[#00FFD1]" /> Instagram
              <ArrowUpRight className="h-4 w-4 text-white/35" />
            </a>
          </div>
        </div>

        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <ListBlock title={about.sectionLabels?.services || 'Services'} items={about.services} />
          <ListBlock title={about.sectionLabels?.clientTypes || 'Client Types'} items={about.clientTypes} />
          <ListBlock title={about.sectionLabels?.markets || 'Markets'} items={about.markets} />
          <ListBlock title={about.sectionLabels?.approach || 'Working Approach'} items={about.approach} />
          <ListBlock title={about.sectionLabels?.technology || 'Technology Approach'} items={about.technology} />

          <section className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-6">
            <h2 className="text-2xl font-semibold text-white">{t('nav.contact')}</h2>
            <div className="mt-5 space-y-4 text-sm leading-6 text-white/62">
              <p>
                {t('common.officialDomain')}:{' '}
                <a href={about.contact.officialDomain} className="text-[#00FFD1] hover:underline">
                  {about.contact.officialDomain}
                </a>
              </p>
              <p className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-[#00FFD1]" />
                <a href={`mailto:${CONTACT_CONFIG.supportEmail}`} className="hover:text-[#00FFD1]">
                  {about.contact.supportEmail}
                </a>
              </p>
              <p className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-[#00FFD1]" />
                <a href={getTelephoneLink()} className="hover:text-[#00FFD1]">
                  {about.contact.phoneDisplay}
                </a>
              </p>
              <p>
                {t('common.instagram')}:{' '}
                <a
                  href={about.contact.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#00FFD1] hover:underline"
                >
                  {about.contact.instagram}
                </a>
              </p>
            </div>
          </section>
        </div>

        <div className="mt-10 grid gap-3 sm:grid-cols-3">
          {[
            { label: t('nav.services'), path: '/services' },
            { label: t('nav.work'), path: '/work' },
            { label: t('nav.contact'), path: '/contact' },
          ].map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="group flex items-center justify-between rounded-xl border border-white/[0.08] bg-white/[0.03] px-4 py-4 text-white/70 hover:border-[#00FFD1]/35 hover:text-white"
            >
              <span>{link.label}</span>
              <ArrowRight className="h-4 w-4 text-[#00FFD1] transition-transform group-hover:translate-x-1" />
            </Link>
          ))}
        </div>

        <p className="mt-10 text-sm text-white/42">{t('common.lastUpdated')}: {about.lastUpdated}</p>
      </div>
    </section>
  </PublicPageShell>
  );
};

export default AboutPage;
